import { useForm, Controller, type SubmitHandler } from "react-hook-form"
import Typography from '@mui/material/Typography'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'

import useFormLocalStorage from "@/hooks/use-form-local-storage"
import { APP_VERSION, YEARS_FROM_START_TO_THIS } from "@/constants/general"
import Form from "@/components/forms/reusables/form"
import useFormStates from "@/hooks/use-form-states"
import type {
    MunicipalityAward,
    MunicipalityAwardFormInput,
} from "@/types"
import {
    useCreateMunicipalityAward,
    useUpdateMunicipalityAward,
} from "@/hooks/api/use-municipality-award"
import RHFTextfield from "../form-fields/rhf-textfield"
import RHFTextarea from "../form-fields/rhf-textarea"

const MunicipalityAwardForm = ({
    municipalityAward,
    closeCallback,
} : {
    municipalityAward?: MunicipalityAward,
    closeCallback?: () => void,
}) => {

    const formDataId = `municipalityAwardForm-${municipalityAward?.id}-v${APP_VERSION}`

    const initialValues = {
        title: municipalityAward ? municipalityAward.title : '',
        year: municipalityAward ? municipalityAward.year : '',
        description: municipalityAward ? municipalityAward.description : '',
    }
    
    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
    } = useForm({
        defaultValues: { ...initialValues },
    })

    const [isLoadedFromLocalStorage, handleFormReset] = useFormLocalStorage({
        control,
        formDataId,
        reset,
        initialValues
    })

    const {
        resetWithVersion,
        requestInProgress,
        setRequestInProgress,
        error,
        setError,
    } = useFormStates(handleFormReset)

    const mutationCreate = useCreateMunicipalityAward({
        setRequestInProgress,
        formDataId,
        onSuccessCallback: closeCallback,
        onErrorCallback(errorMessage) {
            setError(errorMessage)
        }
    })

    const mutationUpdate = useUpdateMunicipalityAward(
        municipalityAward?.id,
        {
            setRequestInProgress,
            formDataId,
            onSuccessCallback: closeCallback,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            }
        },
    )

    const onSubmit: SubmitHandler<MunicipalityAwardFormInput> = data => {
        setRequestInProgress(true)
        if (municipalityAward?.id) {
            mutationUpdate.mutate(data)
        } else {
            mutationCreate.mutate(data)
        }
    }

    return (
        <Form
            onSubmit={handleSubmit(onSubmit)}
            heading={municipalityAward ? 'Upravit ocenění' : 'Přidat nové ocenění'}
            closeCallback={closeCallback}
            isLoadedFromLocalStorage={isLoadedFromLocalStorage}
            isDirty={isDirty}
            resetWithVersion={resetWithVersion}
            requestInProgress={requestInProgress}
            buttonText={municipalityAward ? 'Upravit' : 'Přidat'}
            error={error}
            setError={setError}
        >
            <RHFTextfield
                name="title"
                control={control}
                label="Titulek"
                required
            />
            <FormControl fullWidth>
                <InputLabel id="year-select-label">Rok</InputLabel>
                <Controller
                    name="year"
                    control={control}
                    render={({ field }) => (
                        <Select {...field} label="Rok">
                            <MenuItem key="empty" value="">-</MenuItem>
                            {YEARS_FROM_START_TO_THIS.map(year => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </FormControl>
            <RHFTextarea
                name="description"
                control={control}
                label="Popis"
            />
            {!municipalityAward &&
                <Typography variant="body1" component="p" gutterBottom>
                    Soubory ocenění (PDF, JPG, PNG, apod.) budete moct přidat po vytvoření.
                </Typography>
            }
        </Form>
    )
}

export default MunicipalityAwardForm
